// src/contexts/AlertContext.js
import { createContext, useContext, useRef, useState } from "react";
import AlertModal from "../../../Components/CsplModal/AlertModal";
import { OK } from "../../../Utils/GlobalValues";

/**
 * AlertContext
 *
 * Provides two values:
 *   - showAlert: boolean – whether the alert is currently visible.
 *   - setShowAlert: function – updater to change the visibility.
 *
 */
const AlertContext = createContext({
  showAlert: (title, alertType, message, handleClick) => {},
});

/**
 * AlertProvider
 *
 * Wraps children and manages the `showAlert` state.
 */
export const AlertProvider = ({ children }) => {
  const [showAlertBox, setShowAlertBox] = useState(false);
  const [title, setTitle] = useState("Alert");
  const [alertType, setAlertType] = useState(OK);
  const [message, setMessage] = useState("");

  const onButtonClick = useRef(null);

  const showAlert = (title, alertType, message, handleClick = null) => {
    setTitle(title);
    setAlertType(alertType);
    setMessage(message);
    onButtonClick.current = handleClick;
    setShowAlertBox(true);
  };

  const onhandleClick = () => {
    setShowAlertBox(false);

    if (onButtonClick?.current) {
      onButtonClick.current();
      onButtonClick.current = null;
    }
  };

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}
      <AlertModal
        isModalOpen={showAlertBox}
        setIsModalOpen={setShowAlertBox}
        title={title}
        type={alertType}
        businessProcess={onhandleClick}
        zindex={"200003"}
      >
        {message}
      </AlertModal>
    </AlertContext.Provider>
  );
};

/**
 * Custom hook for consuming the AlertContext.
 *
 * Returns the same values that the provider supplies.
 */
export const useAlertContext = () => useContext(AlertContext);

/**
 * Export the context itself (in case you want to consume it directly).
 */
export default AlertContext;
